import { useRef, useState } from "react";
import { Loader2, ArrowRight, Paperclip, X, AlertCircle, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const POLICY_TYPES = ["Health", "Term / Life", "Personal Accident", "Other"];
export const ISSUES = ["Claim rejected", "Claim partly paid", "Claim delayed / pending", "Cashless denied", "Other"];
const BOUGHT_FROM = ["Online website / app", "Bank", "Agent / advisor", "Directly from insurer", "Other"];
const EMPTY = { name: "", mobile: "", email: "", city: "", policy_type: "", issue_type: "", insurer: "", bought_from: "", claim_amount: "", description: "", website: "" };
const MAX_FILES = 5;
const MAX_BYTES = 15 * 1024 * 1024;
const OK_EXT = ["pdf", "jpg", "jpeg", "png"];
export const inputCls = "w-full px-3.5 py-3 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#5A6880] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/40 focus:border-[#D4A017] transition-[border-color,box-shadow]";
const slug = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const validate = (f) => {
  if (f.name.trim().length < 2) return "Please enter your full name.";
  if (!/^[6-9]\d{9}$/.test(f.mobile)) return "Please enter a valid 10-digit mobile number.";
  if (!f.policy_type) return "Please choose the type of policy.";
  if (!f.issue_type) return "Please choose what happened with your claim.";
  if (f.description.trim().length < 10) return "Please tell us a little about what happened.";
  return "";
};

const Label = ({ children, optional }) => (
  <span className="mb-1.5 flex items-baseline justify-between">
    <span className="text-[13px] font-semibold text-[#1B2A4A]">{children}</span>
    {optional && <span className="text-[11px] text-[#5A6880]">Optional</span>}
  </span>
);

const Chips = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)} data-testid={`${testId}-${slug(o)}`}
        className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${value === o ? "border-[#1B2A4A] bg-[#1B2A4A] text-white" : "border-[#C9D4E6] bg-white text-[#1B2A4A] hover:border-[#D4A017]"}`}>
        {o}
      </button>
    ))}
  </div>
);

const FilePicker = ({ files, setFiles, setError }) => {
  const ref = useRef(null);
  const add = (list) => {
    setError("");
    const next = [...files];
    for (const f of list) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!OK_EXT.includes(ext)) { setError(`'${f.name}' is not allowed. Please attach PDF, JPG or PNG files.`); continue; }
      if (f.size > MAX_BYTES) { setError(`'${f.name}' is too large. Keep each file under 15 MB.`); continue; }
      if (next.length >= MAX_FILES) { setError(`You can attach up to ${MAX_FILES} files.`); break; }
      next.push(f);
    }
    setFiles(next);
  };
  return (
    <div>
      <button type="button" onClick={() => ref.current?.click()} data-testid="ch-attach-btn"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[#C9D4E6] bg-white px-4 py-5 text-center transition-colors hover:border-[#D4A017] hover:bg-[#FFFBF0]">
        <Paperclip className="h-5 w-5 text-[#A87608]" />
        <span className="text-sm font-semibold text-[#1B2A4A]">Attach rejection letter, policy copy or bills</span>
        <span className="text-xs text-[#5A6880]">PDF, JPG or PNG · up to {MAX_FILES} files · 15 MB each</span>
      </button>
      <input ref={ref} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="ch-file-input"
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="ch-file-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-[#EDF1F8] px-3 py-2 text-sm">
              <FileText className="h-4 w-4 shrink-0 text-[#A87608]" />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <button type="button" onClick={() => { setError(""); setFiles(files.filter((_, j) => j !== i)); }} aria-label={`Remove ${f.name}`}
                data-testid={`ch-file-remove-${i}`} className="rounded p-1 text-[#3A4557] hover:bg-white hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const send = (form, files, onProgress) => new Promise((resolve) => {
  const fd = new FormData();
  Object.entries(form).forEach(([k, v]) => fd.append(k, v));
  files.forEach((f) => fd.append("files", f));
  const xhr = new XMLHttpRequest();
  xhr.open("POST", `${API_URL}/api/claim-help/submit`);
  xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
  xhr.onload = () => {
    let data = {};
    try { data = JSON.parse(xhr.responseText); } catch { /* ignore */ }
    resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
  };
  xhr.onerror = () => resolve({ ok: false, data: { detail: "Network error. Please check your connection and try again." } });
  xhr.send(fd);
});

export const ClaimHelpForm = ({ onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate(form);
    setError(msg);
    if (msg) return;
    setProgress(0);
    const res = await send(form, files, setProgress);
    setProgress(null);
    if (res.ok) onDone({ name: form.name, mobile: form.mobile });
    else setError(typeof res.data.detail === "string" ? res.data.detail : "Something went wrong. Please try again.");
  };

  return (
    <form onSubmit={submit} noValidate className="glass-card space-y-5 p-5 sm:p-7" data-testid="claim-help-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><Label>Full name</Label>
          <input className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} autoComplete="name" data-testid="ch-name-input" /></label>
        <label className="block"><Label>Mobile number</Label>
          <span className="relative block"><span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#5A6880]">+91</span>
            <input className={`${inputCls} pl-12`} inputMode="numeric" autoComplete="tel-national" value={form.mobile}
              onChange={(e) => set("mobile")(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="ch-mobile-input" /></span></label>
        <label className="block"><Label optional>Email</Label>
          <input className={inputCls} type="email" autoComplete="email" value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="ch-email-input" /></label>
        <label className="block"><Label optional>City</Label>
          <input className={inputCls} autoComplete="address-level2" value={form.city} onChange={(e) => set("city")(e.target.value)} data-testid="ch-city-input" /></label>
      </div>
      <div><Label>Type of policy</Label><Chips options={POLICY_TYPES} value={form.policy_type} onChange={set("policy_type")} testId="ch-policy-type" /></div>
      <div><Label>What happened with your claim?</Label><Chips options={ISSUES} value={form.issue_type} onChange={set("issue_type")} testId="ch-issue" /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><Label optional>Insurance company</Label>
          <input className={inputCls} value={form.insurer} onChange={(e) => set("insurer")(e.target.value)} data-testid="ch-insurer-input" /></label>
        <label className="block"><Label optional>Claim amount (₹)</Label>
          <input className={inputCls} inputMode="numeric" value={form.claim_amount} onChange={(e) => set("claim_amount")(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="ch-amount-input" /></label>
      </div>
      <div><Label optional>Where did you buy the policy?</Label><Chips options={BOUGHT_FROM} value={form.bought_from} onChange={set("bought_from")} testId="ch-bought-from" /></div>
      <label className="block"><Label>Tell us what happened</Label>
        <textarea className={`${inputCls} min-h-[110px]`} rows={4} value={form.description} onChange={(e) => set("description")(e.target.value)} data-testid="ch-description-input" /></label>
      <div><Label optional>Documents</Label><FilePicker files={files} setFiles={setFiles} setError={setError} /></div>
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" aria-hidden="true" />
      {error && <p className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="ch-form-error"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}</p>}
      <button type="submit" disabled={progress !== null} data-testid="ch-submit-btn"
        className="inline-flex h-13 w-full items-center justify-center gap-2 rounded-xl bg-[#D4A017] py-3.5 text-base font-bold text-[#0A0F1A] shadow-lg shadow-[#D4A017]/25 transition-[transform,background-color] hover:bg-[#c1930f] active:scale-[0.98] disabled:opacity-70">
        {progress !== null ? <><Loader2 className="h-5 w-5 animate-spin" /> {files.length ? `Uploading ${progress}%` : "Sending…"}</> : <>Request my free review <ArrowRight className="h-5 w-5" /></>}
      </button>
      <p className="text-center text-xs text-[#5A6880]">The first review is free. If we take up your case, any fee is discussed with you before we start.</p>
    </form>
  );
};
